import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { FilterDimension } from 'app/hub-components/models/filter-dimension.model';
import { Dataset } from '../../models/analytics-api/dataset.model';
import { Query } from '../../models/analytics-api/query.model';
import { QueryResult } from '../../models/analytics-api/query_result.model';
import { Dimension, Schema } from '../../models/analytics-api/schema.model';
import { SearchResult } from '../../models/analytics-api/search_result.model';
import { SlicesResult } from '../../models/analytics-api/slices_result.model';
import { ApiService } from '../api/api.service';
import { RequestOptionsArgs } from '../backend/backend.service';
import { SessionService } from '../session/session.service';

@Injectable()
export class AnalyticsApiService {
  constructor(private api: ApiService, private sessionService: SessionService) {}

  loadDatasets(options: RequestOptionsArgs = { cache: true }) {
    // Temporal httpHeaders
    options.excludeHeaders = true;
    if (this.sessionService.publicId) {
      options.params ||= {};
      options.params['public_id'] = this.sessionService.publicId;
    }
    return this.api.load('/analytics', options)
      .pipe(
        map(rawDatasets => {
          return (rawDatasets || [])
            .map(dataset => new Dataset(dataset['name'], dataset['type'], dataset['meta']))
            .sortBy((dataset: Dataset) => dataset.displayName);
        }),
      );
  }

  loadSchema(datasetName: string, options: RequestOptionsArgs = { cache: true }) {
    if (this.sessionService.publicId) {
      options.params ||= {};
      options.params['public_id'] = this.sessionService.publicId;
    }
    return this.api.load(`/analytics/${datasetName}/schema`, options)
      .pipe(
        map(rawSchema => new Schema(rawSchema)),
      );
  }

  runQuery(query: Query, options: RequestOptionsArgs = {}): Observable<QueryResult> {
    return this.api.load(`/analytics/${query.getQueryUri()}`, Object.add(options, { operation: 'AnalyticsApiService#runQuery' }))
      .pipe(
        map(result => new QueryResult(query, result)),
      );
  }


  querySlices(query: Query, options: RequestOptionsArgs = {}, filterDimensions?: FilterDimension[]): Observable<SlicesResult> {
    return this.api.load(`/analytics/${query.getSlicesUri(filterDimensions)}`, Object.add(options, { operation: 'AnalyticsApiService#querySlices' }))
      .pipe(
        map(result => new SlicesResult(query, result)),
      );
  }

  search(query: Query, dimension: Dimension, term: string, options: RequestOptionsArgs = { cache: true, skipLoader: true }, limit = 20, data: string[] = undefined) {
    return this.api.load(`/analytics/${query.getSearchUri(dimension, term, limit, data)}`, Object.add(options, { operation: 'AnalyticsApiService#search' }))
      .pipe(
        map(result => new SearchResult(query, result)),
      );
  }
}
